import { useContext, useState, MouseEvent } from 'react';
import {
  Box, Chip, CircularProgress, Popover, Stack, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import {
  LineChart, Line, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../useUser';
import useNswagClient from '../../../hooks/api/useNswagClient';
import useLogError from '../../../hooks/useLogError';
import { ItemDailyHistory } from '../../../app/services/api/generated';
import { applyRoundAndFormatDecimal } from '../../../utils';
import { getOrderableConversionCount } from './itemCardFormatters';

const USAGE_DAYS = 14;

export type UsageSummaryItem = {
  name?: string | null;
  sku?: string | null;
  gtin?: string | null;
  primaryUom?: string | null;
  orderableUom?: string | null;
  packSize?: string | number | null;
  quantityInUom?: number | null;
  caseSize?: number | null;
};

type UsageSummaryPoint = { date: string; usage: number };

type ItemUsageSummary = {
  points: UsageSummaryPoint[];
  totalUsage14Days: number;
  avgDailyUsage14Days: number;
};

const hasText = (value?: string | null): value is string => typeof value === 'string' && value.trim().length > 0;

const buildUsageSummary = (history: ItemDailyHistory[]): ItemUsageSummary => {
  const today = dayjs();
  const dailyUsageByDate = new Map<string, number>();

  history.forEach((entry) => {
    if (!entry.date) {
      return;
    }
    const key = dayjs(entry.date).format('YYYY-MM-DD');
    dailyUsageByDate.set(key, Number(entry.sales ?? 0));
  });

  const points = Array.from({ length: USAGE_DAYS }).map((_, index) => {
    const date = today.subtract(USAGE_DAYS - 1 - index, 'day').format('YYYY-MM-DD');
    return {
      date,
      usage: dailyUsageByDate.get(date) ?? 0,
    };
  });

  const totalUsage14Days = points.reduce((sum, point) => sum + point.usage, 0);

  return {
    points,
    totalUsage14Days,
    avgDailyUsage14Days: totalUsage14Days / USAGE_DAYS,
  };
};

const getUsageValueDisplay = (value: number, item: UsageSummaryItem) => {
  const primaryUom = hasText(item.primaryUom) ? item.primaryUom.trim() : null;
  const orderableUom = hasText(item.orderableUom) ? item.orderableUom.trim() : null;
  const conversionCount = getOrderableConversionCount(item);

  const hasConversion = orderableUom != null && conversionCount != null && conversionCount > 0 && Math.abs(conversionCount - 1) >= 0.0001;

  if (!hasConversion) {
    const primary = orderableUom
      ? `${applyRoundAndFormatDecimal(value)} ${orderableUom}`
      : primaryUom
        ? `${applyRoundAndFormatDecimal(value)} ${primaryUom}`
        : applyRoundAndFormatDecimal(value);

    return { primary, orderable: null as string | null };
  }

  const orderableValue = value / conversionCount!;
  const primary = `${applyRoundAndFormatDecimal(orderableValue)} ${orderableUom}`;
  const orderable = primaryUom ? `(${applyRoundAndFormatDecimal(value)} ${primaryUom})` : null;

  return { primary, orderable };
};

export const UsageSummaryPill = ({ item }: { item: UsageSummaryItem }) => {
  const theme = useTheme();
  const { t } = useTranslation('orderForm');
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.sm}px)`);
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const { getItemDailyHistory } = useNswagClient();
  const { logError } = useLogError();

  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(null);
  const [summary, setSummary] = useState<ItemUsageSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const itemNumber = hasText(item.gtin) ? item.gtin : item.sku;

  if (!hasPermissionTo(['ItemHistoryRead']) || !hasText(itemNumber)) {
    return null;
  }

  const open = Boolean(anchorPosition);

  const handleClose = () => {
    setAnchorPosition(null);
    setError('');
  };

  const handleClick = async (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchorPosition({ top: rect.bottom + 8, left: rect.left });
    setError('');

    if (summary || !selectedStore?.storeNumber) {
      return;
    }

    const fromDate = dayjs().subtract(USAGE_DAYS - 1, 'day').format('YYYY-MM-DD');
    const toDate = dayjs().format('YYYY-MM-DD');

    setLoading(true);
    try {
      const response = await getItemDailyHistory(itemNumber, selectedStore.storeNumber, fromDate, toDate);
      setSummary(buildUsageSummary(response.data ?? []));
    } catch (err) {
      logError(err);
      setError(t('usageSummaryLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const avgUsageDisplay = summary ? getUsageValueDisplay(summary.avgDailyUsage14Days, item) : null;
  const totalUsageDisplay = summary ? getUsageValueDisplay(summary.totalUsage14Days, item) : null;

  return (
    <>
      <Box>
        <Tooltip title={t('usage14Days')}
          arrow>
          <Chip
            clickable
            size="small"
            color="primary"
            variant="outlined"
            icon={<ShowChartIcon fontSize="small" />}
            label={t('usage14DaysCompact')}
            onClick={handleClick}
            sx={{
              borderColor: theme.palette.custom.gray[300],
              color: theme.palette.text.secondary,
              '& .MuiChip-icon': { color: theme.palette.text.secondary },
              '&:hover': {
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.custom.gray[100],
              },
            }}
          />
        </Tooltip>
      </Box>
      <Popover
        open={open}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition ?? undefined}
        onClose={handleClose}
        onClick={(event) => event.stopPropagation()}
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: '1px solid',
            borderColor: theme.palette.custom.gray[300],
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12)',
          },
        }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 4, width: isMobile ? 360 : 520 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="textSM"
              fontWeight={700}>
              {hasText(item.name) ? item.name : t('usageSummaryTitle')}
            </Typography>
            <Typography variant="textXS"
              color="text.secondary"
              sx={{ mt: 0.5 }}>
              {t('usageSummaryWindow', { days: USAGE_DAYS })}
            </Typography>
          </Box>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!loading && error !== '' && (
            <Typography variant="textSM"
              color="error.main"
              sx={{ mt: 4 }}>
              {error}
            </Typography>
          )}

          {!loading && error === '' && summary && (
            <>
              <Stack direction="row"
                spacing={2}
                sx={{ mt: 2.5 }}>
                <Box sx={{ flex: 1, display: 'flex', p: 4, justifyContent: 'space-between', backgroundColor: theme.palette.custom.gray[100], borderRadius: 2, px: 2.5, py: 1.75 }}>
                  <Typography variant="textXS"
                    color="text.secondary">
                    {t('usageAverageDaily')}
                  </Typography>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="textXS"
                      sx={{ display: 'block' }}>
                      {avgUsageDisplay?.primary}
                    </Typography>
                    {avgUsageDisplay?.orderable && (
                      <Typography variant="textXS"
                        sx={{ display: 'block' }}>
                        {avgUsageDisplay.orderable}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ flex: 1, display: 'flex', p: 4, justifyContent: 'space-between', backgroundColor: theme.palette.custom.gray[100], borderRadius: 2, px: 2.5, py: 1.75 }}>
                  <Typography variant="textXS"
                    color="text.secondary">
                    {t('usageTotal14Days')}
                  </Typography>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="textXS"
                      sx={{ display: 'block' }}>
                      {totalUsageDisplay?.primary}
                    </Typography>
                    {totalUsageDisplay?.orderable && (
                      <Typography variant="textXS"
                        sx={{ display: 'block' }}>
                        {totalUsageDisplay.orderable}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Stack>

              <Box sx={{ mt: 2.5, height: 240 }}>
                <ResponsiveContainer width="100%"
                  height="100%">
                  <LineChart data={summary.points}
                    margin={{ top: 12, right: 16, left: 0, bottom: 12 }}>
                    <CartesianGrid strokeDasharray="3 3"
                      stroke={theme.palette.divider} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value: string) => dayjs(value).format('DD MMM')}
                      tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
                      tickLine={false}
                      axisLine={{ stroke: theme.palette.divider }}
                      minTickGap={16}
                    />
                    <YAxis
                      tickFormatter={(value: number) => Math.round(value).toString()}
                      tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
                      tickLine={false}
                      axisLine={{ stroke: theme.palette.divider }}
                      width={44}
                    />
                    <RechartsTooltip
                      labelFormatter={(value: string) => dayjs(value).format('DD MMM')}
                      content={({ active, payload, label }) => {
                        if (!active || !payload || payload.length === 0) {
                          return null;
                        }

                        const rawValue = typeof payload[0].value === 'number'
                          ? payload[0].value
                          : Number(payload[0].value ?? 0);

                        const usageDisplay = getUsageValueDisplay(rawValue, item);

                        return (
                          <Box sx={{ borderRadius: 3, border: '1px solid', borderColor: theme.palette.custom.gray[300], backgroundColor: theme.palette.common.white, boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)', px: 2, py: 1.5 }}>
                            <Typography variant="textXS"
                              sx={{ mb: 0.75 }}>
                              {t('usageLabel')} {typeof label === 'string' ? dayjs(label).format('DD MMM') : ''}
                            </Typography>
                            <Box>
                              <Typography variant="textXS"
                                sx={{ lineHeight: 1.35, display: 'block' }}>
                                {usageDisplay.primary}
                              </Typography>
                              {usageDisplay.orderable && (
                                <Typography variant="textXS"
                                  sx={{ pt: 0.25, lineHeight: 1.35, display: 'block' }}>
                                  {usageDisplay.orderable}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        );
                      }}
                      contentStyle={{ borderRadius: 12, borderColor: theme.palette.custom.gray[300], boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="usage"
                      stroke={theme.palette.primary.main}
                      strokeWidth={2.5}
                      dot={{ r: 2, strokeWidth: 0, fill: theme.palette.primary.main }}
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>

              <Typography variant="textXS"
                color="text.secondary"
                sx={{ mt: 1.5 }}>
                {t('usageSummaryHint')}
              </Typography>
            </>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default UsageSummaryPill;
